import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { authApi } from '../api/auth'
import { ApiError } from '../lib/api'
import { Spinner } from '../components/Spinner'

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  reset: (id?: string) => void
}
function getTurnstile(): TurnstileApi | undefined {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile
}

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
function loadTurnstileScript(): Promise<void> {
  if (getTurnstile()) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('turnstile load')))
      return
    }
    const s = document.createElement('script')
    s.src = TURNSTILE_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('turnstile load'))
    document.head.appendChild(s)
  })
}

export default function Login() {
  const { user, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation() as { state?: { from?: string } }

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Cloudflare Turnstile (captcha). Só é exigido se o ambiente tiver Site Key.
  const [siteKey, setSiteKey] = useState<string | null>(null)
  const [token, setToken] = useState('')
  const turnstileRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    authApi
      .turnstileConfig()
      .then((r) => setSiteKey(r.siteKey))
      .catch(() => setSiteKey(null))
  }, [])

  useEffect(() => {
    if (!siteKey) return
    let cancelled = false
    loadTurnstileScript()
      .then(() => {
        const ts = getTurnstile()
        if (cancelled || !ts || !turnstileRef.current || widgetIdRef.current) return
        widgetIdRef.current = ts.render(turnstileRef.current, {
          sitekey: siteKey,
          theme: 'dark',
          callback: (t: string) => setToken(t),
          'expired-callback': () => setToken(''),
          'error-callback': () => setToken(''),
        })
      })
      .catch(() => setError('Não foi possível carregar a verificação de segurança.'))
    return () => {
      cancelled = true
    }
  }, [siteKey])

  // Vídeo de fundo só é baixado/iniciado DEPOIS do load da página (em tempo
  // ocioso), para não competir com FCP/LCP. Até lá fica só a imagem (poster).
  const videoRef = useRef<HTMLVideoElement>(null)
  const [videoOn, setVideoOn] = useState(false)

  useEffect(() => {
    let cancelled = false
    const start = () => {
      const v = videoRef.current
      if (cancelled || !v) return
      if (!v.src) {
        v.src = 'https://assets.lucrodigital.com.br/ld-analytics/Video_cover03.webm'
        v.load()
      }
      v.play().catch(() => {
        /* autoplay pode ser bloqueado — segue com a imagem */
      })
    }
    const schedule = () => {
      const ric = (window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void
      }).requestIdleCallback
      if (ric) ric(start, { timeout: 3000 })
      else window.setTimeout(start, 1200)
    }
    if (document.readyState === 'complete') schedule()
    else window.addEventListener('load', schedule, { once: true })
    return () => {
      cancelled = true
      window.removeEventListener('load', schedule)
    }
  }, [])

  if (user) return <Navigate to="/" replace />

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await login(email, password, token || undefined)
      navigate(location.state?.from ?? '/', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível entrar')
      // O token do Turnstile é de uso único — reseta o widget para um novo desafio.
      if (siteKey && widgetIdRef.current) {
        getTurnstile()?.reset(widgetIdRef.current)
        setToken('')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink-gradient px-4">
      {/* Imagem de fundo (carrega primeiro — candidata a LCP). */}
      <img
        src="https://assets.lucrodigital.com.br/ld-analytics/LD_Analytics_Cover_3641_2048.webp"
        alt=""
        fetchPriority="high"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      {/* Vídeo: só baixa/roda após o load (ver useEffect); aparece com fade
          por cima da imagem para não atrasar o first paint. */}
      <video
        ref={videoRef}
        className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
          videoOn ? 'opacity-100' : 'opacity-0'
        }`}
        loop
        muted
        playsInline
        preload="none"
        onPlaying={() => setVideoOn(true)}
      />
      {/* Overlay escuro para manter o formulário legível sobre o fundo */}
      <div className="pointer-events-none absolute inset-0 bg-ink-900/70" />

      {/* Camada de grid + glows */}
      <div className="pointer-events-none absolute inset-0 bg-grid [background-size:34px_34px] opacity-50" />
      <div className="pointer-events-none absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-brand-500/30 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-1/4 h-72 w-72 rounded-full bg-accent-500/25 blur-3xl" />

      <div className="relative w-full max-w-sm animate-fade-up">
        <div className="mb-7 text-center">
          <div className="relative mx-auto mb-4 inline-block">
            <div className="absolute -inset-2 rounded-3xl bg-brand-gradient opacity-60 blur-lg" />
            <img
              src="/img/Logo_960px_3D.webp"
              alt="Lucro Digital"
              className="relative h-20 w-20 rounded-3xl object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-white">Lucro Digital Analytics</h1>
          <p className="mt-1 text-sm text-slate-400">Inteligência de campanhas com IA</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur-xl">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-300">Email</label>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-slate-500 transition focus:border-brand-400 focus:outline-none focus:ring-4 focus:ring-brand-500/20"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-300">Senha</label>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-slate-500 transition focus:border-brand-400 focus:outline-none focus:ring-4 focus:ring-brand-500/20"
              />
            </div>

            {/* Cloudflare Turnstile — renderizado só quando há Site Key. */}
            {siteKey && <div ref={turnstileRef} className="flex justify-center" />}

            {error && (
              <p className="rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || (!!siteKey && !token)}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white shadow-glow transition hover:brightness-110 active:brightness-95 disabled:opacity-50"
            >
              {loading && <Spinner className="h-4 w-4 text-white" />}
              Entrar
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Lucro Digital Analytics
        </p>
      </div>
    </div>
  )
}
